import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Icon from "@material-ui/core/Icon";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { TwoLineCell, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const METHODS = ["Cash", "Bank", "MTN MoMo", "Airtel Money"];
const RESTRICTED_ROLES = ["Bursar", "Principal"];

const METHOD_COLORS = { Cash: "#e8f5e9", Bank: "#e3f2fd", "MTN MoMo": "#fff8e1", "Airtel Money": "#fce4ec" };
const METHOD_TEXT   = { Cash: "#2e7d32", Bank: "#0d47a1", "MTN MoMo": "#e65100", "Airtel Money": "#880e4f" };

const BLANK_FORM = { studentId: "", invoiceId: "", amount: "", method: "Cash", referenceNo: "", notes: "" };

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "240px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },
  heroActions: { position: "absolute", top: "40px", right: "40px", zIndex: 3, display: "flex", gap: "12px" },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "220px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "18px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "11px", color: "#64748b", margin: "2px 0 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" },
  statSub: { fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" },

  // Toolbar
  toolbarWrap: {
    background: "#f8fafc",
    padding: "16px 24px",
    borderRadius: "16px 16px 0 0",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px"
  },
  resultsCount: { fontSize: "13px", color: "#64748b", fontWeight: 500 },

  method:     { display: "inline-block", padding: "4px 10px", borderRadius: "8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" },
  errorMsg:   { color: "#f44336", fontSize: "13px", marginBottom: "8px" },
  formControl: { width: "100%", marginTop: "16px" },
  // Dialog Styles
  dialogSection: { marginBottom: "24px" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "18px",
    paddingBottom: "14px",
    borderBottom: "1px solid #f1f5f9"
  },
  sectionIconBadge: {
    width: "34px", height: "34px", borderRadius: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0
  },
  sectionHeading: { fontSize: "14px", fontWeight: 800, color: "#1e293b", margin: 0, letterSpacing: "-0.1px" },
  sectionHint: { fontSize: "12px", color: "#94a3b8", fontWeight: 500, marginLeft: "auto" },
  lockedCard: {
    display: "flex", alignItems: "center", gap: "14px",
    background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: "14px",
    padding: "22px 20px", color: "#94a3b8"
  },
  lockedIcon: {
    width: "36px", height: "36px", borderRadius: "50%", background: "#eef2f6",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#94a3b8"
  },
  searchBox: {
    position: "relative",
    background: "#f8fafc",
    padding: "4px 16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "all 0.2s ease",
    "&:focus-within": { borderColor: "#3949ab", boxShadow: "0 0 0 3px rgba(57, 73, 171, 0.1)", background: "#fff" }
  },
  searchIcon: { color: "#94a3b8", fontSize: "20px" },
  searchResults: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
    zIndex: 100,
    maxHeight: "240px",
    overflowY: "auto",
    padding: "8px"
  },
  searchResultItem: {
    padding: "10px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    transition: "all 0.2s ease",
    "&:hover": { background: "#f1f5f9" }
  },
  studentSummary: {
    display: "flex", alignItems: "center", gap: "12px",
    background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "12px",
    padding: "12px 16px", marginTop: "10px"
  },
  studentAvatar: {
    width: "38px", height: "38px", borderRadius: "50%", background: "#1a237e", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "14px", flexShrink: 0
  },
  stepBar: { display: "flex", alignItems: "center", marginBottom: "28px" },
  stepDot: {
    width: "26px", height: "26px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "13px", fontWeight: 800, flexShrink: 0, transition: "all 0.2s ease"
  },
  stepLabel: { fontSize: "11px", fontWeight: 700, marginLeft: "8px", textTransform: "uppercase", letterSpacing: "0.4px" },
  stepLine: { flex: 1, height: "2px", margin: "0 12px", transition: "all 0.2s ease" },
  balancePreview: {
    marginTop: "8px", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px"
  },
  invoiceCard: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    marginBottom: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": { borderColor: "#3949ab", background: "#f8fafc" }
  },
  invoiceSelected: { borderColor: "#1a237e", background: "#eef2ff", boxShadow: "0 0 0 1px #1a237e" },
  receiptBox: {
    background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
    border: "1px solid #bbf7d0",
    borderRadius: "16px",
    padding: "32px 24px",
    textAlign: "center"
  },
  receiptIcon: {
    width: "56px",
    height: "56px",
    background: "#22c55e",
    color: "#fff",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
    fontSize: "32px",
    boxShadow: "0 4px 12px rgba(34, 197, 94, 0.3)"
  },
  receiptAmount: { fontSize: "28px", fontWeight: 800, color: "#166534", margin: "0 0 8px" },
  receiptDetail: { fontSize: "14px", color: "#166534", opacity: 0.8, margin: 0 },
  tableCard: { borderRadius: "20px", boxShadow: "0 10px 30px -12px rgba(0,0,0,0.1)", border: "none", overflow: "hidden" },
  entityName: { fontWeight: 700, color: "#1a237e", fontSize: "14px" },
};

class PaymentList extends Component {
  state = {
    tab: 0, payments: [], total: 0, pagination: { pageIndex: 0, pageSize: 20 }, totalPages: 1, loading: true, error: null,
    open: false,
    form: { ...BLANK_FORM },
    studentSearch: "", searchResults: [], searching: false,
    selectedStudent: null, invoices: [], selectedInvoice: null,
    saving: false, formError: null, receipt: null
  };

  getUserRole() {
    try { return JSON.parse(localStorage.getItem("bwillis_user") || "{}").role || ""; }
    catch { return ""; }
  }

  componentDidMount() { this.fetchPayments(); }

  fetchPayments = async (pagination = this.state.pagination) => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/payments", { params: { page: pagination.pageIndex + 1, limit: pagination.pageSize } });
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : (d.data || []);
      this.setState({ payments: list, total: d.total || list.length, totalPages: d.totalPages || 1, pagination, loading: false });
    } catch { this.setState({ error: "Could not load payments.", loading: false }); }
  };

  handlePaginationChange = updater => {
    const next = typeof updater === "function" ? updater(this.state.pagination) : updater;
    this.fetchPayments(next);
  };

  openDialog = () => this.setState({
    open: true, form: { ...BLANK_FORM },
    studentSearch: "", searchResults: [], selectedStudent: null,
    invoices: [], selectedInvoice: null, formError: null, receipt: null
  });

  closeDialog = () => this.setState({ open: false });

  handleChange = field => e => {
    const value = e?.target?.value ?? "";
    this.setState(prev => ({ form: Object.assign({}, prev.form, { [field]: value }), formError: null }));
  };

  searchStudents = async () => {
    if (!this.state.studentSearch.trim()) return;
    this.setState({ searching: true });
    try {
      const res = await api.get("/students", { params: { search: this.state.studentSearch, limit: 10, status: "active" } });
      const d = res.data.data || res.data;
      this.setState({ searchResults: Array.isArray(d) ? d : (d.data || []), searching: false });
    } catch { this.setState({ searching: false }); }
  };

  selectStudent = async (student) => {
    this.setState({
      selectedStudent: student, searchResults: [],
      studentSearch: `${student.firstName} ${student.lastName}`
    });
    this.setState(prev => ({ form: Object.assign({}, prev.form, { studentId: student.id }) }));
    try {
      const res = await api.get("/fees/invoices", { params: { studentId: student.id } });
      const d = res.data.data || res.data;
      this.setState({ invoices: Array.isArray(d) ? d : (d.data || []) });
    } catch { /* silent */ }
  };

  clearStudent = () => {
    this.setState(prev => ({
      selectedStudent: null, invoices: [], selectedInvoice: null,
      studentSearch: "", searchResults: [],
      form: Object.assign({}, prev.form, { studentId: "", invoiceId: "", amount: "" })
    }));
  };

  selectInvoice = (inv) => {
    this.setState({ selectedInvoice: inv });
    this.setState(prev => ({ form: Object.assign({}, prev.form, { invoiceId: inv.id, amount: String(inv.balanceDue || "") }) }));
  };

  handleSubmit = async () => {
    const { form } = this.state;
    if (!form.studentId || !form.amount || parseInt(form.amount, 10) <= 0) {
      this.setState({ formError: "Student and a valid amount are required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      const res = await api.post("/payments", { ...form, amount: parseInt(form.amount, 10) });
      const data = res.data.data || res.data;
      this.setState({ saving: false, receipt: data.receipt || { receiptNo: "—" } });
      this.fetchPayments();
    } catch (err) {
      this.setState({ formError: err.response?.data?.message || "Failed to record payment.", saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      payments, loading, error, pagination, total,
      open, form, studentSearch, searchResults, searching,
      selectedStudent, invoices, selectedInvoice,
      saving, formError, receipt
    } = this.state;

    const canRecord = RESTRICTED_ROLES.includes(this.getUserRole());

    // Calculate basic metrics for KPI cards
    const totalCollected = payments.reduce((acc, p) => acc + (p.amount || 0), 0);
    const averagePayment = payments.length > 0 ? totalCollected / payments.length : 0;

    const columns = [
      {
        id: "receiptNo",
        header: "Receipt No",
        accessorFn: p => (p.receipt ? p.receipt.receiptNo : "—"),
        meta: { variant: "mono" },
        cell: info => <span style={{ fontWeight: 600, color: "#475569" }}>{info.getValue()}</span>
      },
      {
        id: "student",
        header: "Student",
        accessorFn: p => (p.student ? `${p.student.firstName} ${p.student.lastName}` : "—"),
        cell: info => <TwoLineCell primary={info.getValue()} secondary={info.row.original.student?.studentNo} />
      },
      {
        id: "amount",
        header: "Amount (UGX)",
        accessorFn: p => p.amount || 0,
        meta: { align: "right" },
        cell: info => <strong style={{ color: "#1a237e", fontSize: "15px" }}>{formatUGX(info.getValue())}</strong>
      },
      {
        id: "method",
        header: "Method",
        accessorFn: p => p.method || "Cash",
        cell: info => {
          const method = info.getValue();
          return <span className={classes.method} style={{ background: METHOD_COLORS[method] || "#f5f5f5", color: METHOD_TEXT[method] || "#333" }}>{method}</span>;
        }
      },
      {
        id: "reference",
        header: "Reference",
        accessorFn: p => p.referenceNo || "—",
        meta: { variant: "mono" }
      },
      {
        id: "date",
        header: "Date",
        accessorFn: p => (p.receivedAt ? new Date(p.receivedAt).toLocaleDateString("en-UG", { day: '2-digit', month: 'short', year: 'numeric' }) : "—")
      },
      {
        id: "receivedBy",
        header: "Received By",
        accessorFn: p => (p.receivedByUser ? `${p.receivedByUser.firstName} ${p.receivedByUser.lastName}` : "—")
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Payment Records</h1>
              <p className={classes.heroSub}>
                Institutional ledger for all fee collections and financial transactions
              </p>
            </div>
            {canRecord && (
              <div className={classes.heroActions}>
                <Button color="white" onClick={this.openDialog}>
                  <Icon style={{ marginRight: "8px" }}>add_circle</Icon> Record Payment
                </Button>
              </div>
            )}
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(26, 35, 126, 0.1)", color: "#1a237e" }}>
                <Icon>account_balance_wallet</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{total.toLocaleString()}</p>
                <p className={classes.statLabel}>Total Transactions</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(76, 175, 80, 0.1)", color: "#2e7d32" }}>
                <Icon>payments</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>UGX {totalCollected.toLocaleString()}</p>
                <p className={classes.statLabel}>Total Collected (View)</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(255, 152, 0, 0.1)", color: "#ef6c00" }}>
                <Icon>trending_up</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>UGX {Math.round(averagePayment).toLocaleString()}</p>
                <p className={classes.statLabel}>Avg. Transaction</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.toolbarWrap}>
            <ViewToolbar
              tabs={["All Payments", "Pending", "Cleared"]}
              activeTab={this.state.tab || 0}
              onTabChange={tab => this.setState({ tab })}
              actions={[]}
            />
            <div className={classes.resultsCount}>
              Showing {payments.length} of {total} records
            </div>
          </div>

          {error && <div style={{ padding: "20px", color: "#f44336", textAlign: "center" }}>⚠ {error}</div>}

          <DataTable
            columns={columns}
            data={payments}
            isLoading={loading}
            emptyState="No payments recorded yet."
            pagination={pagination}
            onPaginationChange={this.handlePaginationChange}
            totalCount={total}
          />
        </GridItem>

        {/* Record Payment Dialog — restricted to Bursar / Principal */}
        <Dialog open={open} onClose={this.closeDialog} maxWidth="sm" fullWidth scroll="body">
          <DialogHeader
            title="Record Payment"
            subtitle="Finalize fee collection and generate institutional receipt"
          />
          <DialogContent style={{ padding: "24px" }}>
            {receipt ? (
              <div className={classes.receiptBox}>
                <div className={classes.receiptIcon}>
                  <Icon>check</Icon>
                </div>
                <h3 className={classes.receiptAmount}>
                  UGX {parseInt(form.amount, 10).toLocaleString()}
                </h3>
                <p className={classes.receiptDetail}>
                  Receipt No: <strong>{receipt.receiptNo}</strong>
                </p>
                {selectedStudent && (
                  <p className={classes.receiptDetail} style={{ marginTop: "4px" }}>
                    Collected from: <strong>{selectedStudent.firstName} {selectedStudent.lastName}</strong>
                  </p>
                )}
              </div>
            ) : (
              <>
                {formError && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fee2e2", borderRadius: "10px", padding: "12px 16px", color: "#b91c1c", fontSize: "13px", display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                    <Icon style={{ fontSize: "18px" }}>error_outline</Icon>
                    {formError}
                  </div>
                )}

                {/* Step progress: Student → Invoice → Payment */}
                <div className={classes.stepBar}>
                  {[
                    { label: "Student", done: !!selectedStudent },
                    { label: "Invoice", done: !!selectedInvoice, optional: invoices.length === 0 },
                    { label: "Payment", done: !!form.amount && parseInt(form.amount, 10) > 0 }
                  ].map((step, idx, arr) => {
                    const active = step.done || (idx === 0) || arr[idx - 1]?.done;
                    return (
                      <React.Fragment key={step.label}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div className={classes.stepDot} style={{
                            background: step.done ? "#22c55e" : active ? "#1a237e" : "#e2e8f0",
                            color: step.done || active ? "#fff" : "#94a3b8"
                          }}>
                            {step.done ? <Icon style={{ fontSize: "16px" }}>check</Icon> : idx + 1}
                          </div>
                          <span className={classes.stepLabel} style={{ color: step.done || active ? "#1a237e" : "#94a3b8" }}>
                            {step.label}{step.optional ? " (optional)" : ""}
                          </span>
                        </div>
                        {idx < arr.length - 1 && (
                          <div className={classes.stepLine} style={{ background: arr[idx].done ? "#22c55e" : "#e2e8f0" }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Section 1: Student Selection */}
                <div className={classes.dialogSection}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.sectionIconBadge} style={{ background: "#e8eaf6", color: "#3949ab" }}>
                      <Icon style={{ fontSize: "18px" }}>person</Icon>
                    </div>
                    <h4 className={classes.sectionHeading}>Student Details</h4>
                  </div>

                  {selectedStudent ? (
                    <div className={classes.studentSummary}>
                      <div className={classes.studentAvatar}>
                        {`${selectedStudent.firstName?.[0] || ""}${selectedStudent.lastName?.[0] || ""}`.toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "14px" }}>
                          {selectedStudent.firstName} {selectedStudent.lastName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          {selectedStudent.studentNo} • {selectedStudent.programme?.name || "N/A"}
                        </div>
                      </div>
                      <Button size="sm" simple color="rose" onClick={this.clearStudent} style={{ padding: "6px 10px" }}>
                        <Icon style={{ fontSize: "16px", marginRight: "4px" }}>close</Icon> Change
                      </Button>
                    </div>
                  ) : (
                    <div className={classes.searchBox}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Icon className={classes.searchIcon}>search</Icon>
                        <CustomInput
                          labelText="Search Student (name or registration no)"
                          id="dlgStudentSearch"
                          formControlProps={{ fullWidth: true, style: { marginTop: 0, marginBottom: 0 } }}
                          inputProps={{
                            value: studentSearch,
                            onChange: e => this.setState({ studentSearch: e.target.value }),
                            onKeyPress: e => e.key === "Enter" && (e.preventDefault(), this.searchStudents()),
                            autoComplete: "off",
                            style: { padding: "10px 0" }
                          }}
                        />
                        {studentSearch && (
                          <Button size="sm" color="info" simple onClick={this.searchStudents} disabled={searching} style={{ padding: "8px" }}>
                            {searching ? <CircularProgress size={14} /> : "Search"}
                          </Button>
                        )}
                      </div>

                      {searchResults.length > 0 && (
                        <div className={classes.searchResults}>
                          {searchResults.map(s => (
                            <div key={s.id} className={classes.searchResultItem} onClick={() => this.selectStudent(s)}>
                              <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>{s.firstName} {s.lastName}</span>
                              <span style={{ fontSize: "11px", color: "#64748b" }}>{s.studentNo} • {s.programme?.name || "N/A"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Section 2: Invoice Selection (Optional but recommended) */}
                {invoices.length > 0 && (
                  <div className={classes.dialogSection}>
                    <div className={classes.sectionHeader}>
                      <div className={classes.sectionIconBadge} style={{ background: "#f3e5f5", color: "#7b1fa2" }}>
                        <Icon style={{ fontSize: "18px" }}>receipt_long</Icon>
                      </div>
                      <h4 className={classes.sectionHeading}>Select Invoice</h4>
                    </div>
                    <div style={{ maxHeight: "160px", overflowY: "auto", paddingRight: "4px" }}>
                      {invoices.map(inv => {
                        const isSelected = selectedInvoice && selectedInvoice.id === inv.id;
                        return (
                          <div key={inv.id} className={`${classes.invoiceCard} ${isSelected ? classes.invoiceSelected : ""}`} onClick={() => this.selectInvoice(inv)}>
                            <div>
                              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>{inv.semester ? inv.semester.name : "Semester Bill"}</div>
                              <div style={{ fontSize: "11px", color: "#64748b" }}>Due: UGX {(inv.amount || 0).toLocaleString()}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "14px", fontWeight: 800, color: inv.balanceDue > 0 ? "#ef4444" : "#22c55e" }}>
                                UGX {(inv.balanceDue || 0).toLocaleString()}
                              </div>
                              <div style={{ fontSize: "10px", color: "#94a3b8", textTransform: "uppercase" }}>Balance</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Section 3: Payment Details */}
                <div className={classes.dialogSection}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.sectionIconBadge} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                      <Icon style={{ fontSize: "18px" }}>payments</Icon>
                    </div>
                    <h4 className={classes.sectionHeading}>Payment Information</h4>
                    {!selectedStudent && <span className={classes.sectionHint}>Locked until a student is selected</span>}
                  </div>

                  {!selectedStudent ? (
                    <div className={classes.lockedCard}>
                      <div className={classes.lockedIcon}><Icon style={{ fontSize: "18px" }}>lock</Icon></div>
                      <span>Select a student above to enter the payment amount, method, and reference.</span>
                    </div>
                  ) : (
                    <GridContainer>
                      <GridItem xs={12} sm={6}>
                        <CustomInput labelText="Amount (UGX) *" id="dlgAmount" formControlProps={{ fullWidth: true }}
                          inputProps={{ value: form.amount, onChange: this.handleChange("amount"), type: "number", startAdornment: <Icon style={{ marginRight: "8px", color: "#94a3b8", fontSize: "18px" }}>payments</Icon> }} />
                        {selectedInvoice && form.amount && (() => {
                          const remaining = (selectedInvoice.balanceDue || 0) - parseInt(form.amount, 10);
                          const overpaying = remaining < 0;
                          return (
                            <div className={classes.balancePreview} style={{ color: overpaying ? "#ef4444" : "#22c55e" }}>
                              <Icon style={{ fontSize: "15px" }}>{overpaying ? "warning" : "info"}</Icon>
                              {overpaying
                                ? `Exceeds balance by UGX ${Math.abs(remaining).toLocaleString()}`
                                : `Balance after payment: UGX ${remaining.toLocaleString()}`}
                            </div>
                          );
                        })()}
                      </GridItem>
                      <GridItem xs={12} sm={6}>
                        <FormControl className={classes.formControl} style={{ marginTop: "12px" }}>
                          <InputLabel>Payment Method</InputLabel>
                          <Select value={form.method} onChange={this.handleChange("method")}>
                            {METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </GridItem>
                      <GridItem xs={12}>
                        <CustomInput labelText="Reference No (cheque / MoMo ref)" id="dlgRef" formControlProps={{ fullWidth: true }}
                          inputProps={{ value: form.referenceNo, onChange: this.handleChange("referenceNo"), startAdornment: <Icon style={{ marginRight: "8px", color: "#94a3b8", fontSize: "18px" }}>tag</Icon> }} />
                      </GridItem>
                      <GridItem xs={12}>
                        <CustomInput labelText="Notes / Internal Remarks" id="dlgNotes" formControlProps={{ fullWidth: true }}
                          inputProps={{ value: form.notes, onChange: this.handleChange("notes"), multiline: true, rows: 2 }} />
                      </GridItem>
                    </GridContainer>
                  )}
                </div>
              </>
            )}
          </DialogContent>
          {receipt ? (
            <DialogFooter>
              <Button simple onClick={() => this.setState({
                receipt: null, form: { ...BLANK_FORM },
                selectedStudent: null, invoices: [], selectedInvoice: null, studentSearch: ""
              })}>
                New Payment
              </Button>
              <Button color="primary" onClick={this.closeDialog}>Done</Button>
            </DialogFooter>
          ) : (
            <DialogFooter note="Restricted · Bursar / Principal only" noteIcon="lock">
              <Button outlined onClick={this.closeDialog} disabled={saving}>Cancel</Button>
              <Button color="primary" onClick={this.handleSubmit} disabled={saving}>
                {saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Record Payment"}
              </Button>
            </DialogFooter>
          )}
        </Dialog>
      </GridContainer>
    );
  }
}

PaymentList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(PaymentList));
